import { useState, useContext, useMemo } from 'react';
import { AuthContext } from '@/context/auth.context';
import {
  useCreateCredential,
  useUpdateCredential,
} from '@/api/rpc/hooks/credentials/useCredentials';
import type { CredentialSummaryResponse } from '@erp/rpc';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { T, useGT } from 'gt-react';
import {
  credentialAutoPauseThresholdSchema,
  DEFAULT_CREDENTIAL_AUTO_PAUSE_REJECTED_RUNS,
  MAX_CREDENTIAL_AUTO_PAUSE_REJECTED_RUNS,
} from '@erp/shared-types';
import { CredentialAutoPauseFields } from './CredentialAutoPauseFields';
import {
  buildCredentialFormSubmission,
  isCredentialTypeValue,
  type CredentialTypeValue,
} from './credentialFormSubmission';

interface CredentialFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** If provided, dialog opens in edit mode with pre-filled values */
  credential?: CredentialSummaryResponse;
}

export function CredentialFormDialog({
  open,
  onClose,
  credential,
}: CredentialFormDialogProps) {
  const gt = useGT();
  const isEditMode = Boolean(credential);
  const { currentUser } = useContext(AuthContext);
  const organizationId = currentUser?.organizationId;

  const [name, setName] = useState(credential?.name ?? '');
  const [type, setType] = useState<CredentialTypeValue>(
    credential?.type && isCredentialTypeValue(credential.type)
      ? credential.type
      : 'password',
  );
  const [username, setUsername] = useState(
    (credential?.metadata?.username as string) ?? '',
  );
  const [accessKeyId, setAccessKeyId] = useState(
    (credential?.metadata?.accessKeyId as string) ?? '',
  );
  const [secret, setSecret] = useState('');
  const [autoPauseEnabled, setAutoPauseEnabled] = useState(
    credential?.autoPauseAfterRejectedRuns != null,
  );
  const [autoPauseThreshold, setAutoPauseThreshold] = useState(
    String(
      credential?.autoPauseAfterRejectedRuns ??
        DEFAULT_CREDENTIAL_AUTO_PAUSE_REJECTED_RUNS,
    ),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const credentialTypes = useMemo(
    () => [
      {
        value: 'password' as const,
        label: gt('Password', {
          context: 'Organization credential type: password',
        }),
        description: gt('Username + password', {
          context: 'Organization credential type description: password',
        }),
      },
      {
        value: 'api_token' as const,
        label: gt('API Token', {
          context: 'Organization credential type: api_token',
        }),
        description: gt('Bearer / API token', {
          context: 'Organization credential type description: api_token',
        }),
      },
      {
        value: 'oauth_client' as const,
        label: gt('OAuth Client', {
          context: 'Organization credential type: oauth_client',
        }),
        description: gt('OAuth client credentials', {
          context: 'Organization credential type description: oauth_client',
        }),
      },
      {
        value: 'certificate' as const,
        label: gt('Certificate', {
          context: 'Organization credential type: certificate',
        }),
        description: gt('TLS/mTLS certificate or private key', {
          context: 'Organization credential type description: certificate',
        }),
      },
      {
        value: 'azure_blob' as const,
        label: gt('Azure Blob Storage', {
          context: 'Organization credential type: azure_blob',
        }),
        description: gt('Azure Blob Storage connection string', {
          context: 'Organization credential type description: azure_blob',
        }),
      },
      {
        value: 'aws' as const,
        label: gt('AWS', {
          context: 'Organization credential type: aws',
        }),
        description: gt('Access key ID + secret access key', {
          context: 'Organization credential type description: aws',
        }),
      },
    ],
    [gt],
  );

  function resetForm() {
    setName('');
    setType('password');
    setUsername('');
    setAccessKeyId('');
    setSecret('');
    setAutoPauseEnabled(false);
    setAutoPauseThreshold(String(DEFAULT_CREDENTIAL_AUTO_PAUSE_REJECTED_RUNS));
    setErrors({});
  }

  const createCredential = useCreateCredential({
    onSuccess: () => {
      resetForm();
      onClose();
    },
  });

  const updateCredential = useUpdateCredential({
    onSuccess: () => {
      resetForm();
      onClose();
    },
  });

  function validateForm(): boolean {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = gt('Name is required', {
        context: 'Credential form validation',
      });
    } else if (name.length > 255) {
      newErrors.name = gt('Must not exceed 255 characters', {
        context: 'Credential form validation',
      });
    }

    // Secret is required only when creating, optional when editing
    if (!isEditMode && !secret.trim()) {
      newErrors.secret = gt('Secret / password is required', {
        context: 'Credential form validation',
      });
    }

    if (
      !credentialAutoPauseThresholdSchema.safeParse(
        autoPauseEnabled ? Number(autoPauseThreshold) : null,
      ).success
    ) {
      newErrors.autoPauseThreshold = gt(
        'Enter a whole number from 1 to {max}.',
        {
          context: 'Credential auto-pause threshold validation',
          max: String(MAX_CREDENTIAL_AUTO_PAUSE_REJECTED_RUNS),
        },
      );
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit() {
    if (!validateForm()) return;
    if (!organizationId) {
      setErrors({
        form: gt('Organization context is unavailable. Please refresh.', {
          context: 'Credential form error when org missing',
        }),
      });
      return;
    }

    const autoPauseAfterRejectedRuns = credentialAutoPauseThresholdSchema.parse(
      autoPauseEnabled ? Number(autoPauseThreshold) : null,
    );
    const submission = buildCredentialFormSubmission({
      organizationId,
      credential,
      type,
      name,
      username,
      accessKeyId,
      secret,
      autoPauseAfterRejectedRuns,
    });
    if (submission.mode === 'edit') {
      updateCredential.mutate(submission.input, {
        onError: (error) => {
          setErrors({
            form:
              error.message ||
              gt('Failed to update credential', {
                context: 'Credential save error',
              }),
          });
        },
      });
    } else {
      createCredential.mutate(submission.input, {
        onError: (error) => {
          setErrors({
            form:
              error.message ||
              gt('Failed to save credential', {
                context: 'Credential save error',
              }),
          });
        },
      });
    }
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      resetForm();
      onClose();
    }
  }

  const secretLabel =
    type === 'password'
      ? gt('Password', { context: 'Credential secret field label' })
      : type === 'api_token'
        ? gt('API Token', { context: 'Credential secret field label' })
        : type === 'oauth_client'
          ? gt('Client Secret', { context: 'Credential secret field label' })
          : type === 'certificate'
            ? gt('Certificate / Key', {
                context: 'Credential secret field label',
              })
            : type === 'azure_blob'
              ? gt('Connection String', {
                  context: 'Credential secret field label',
                })
              : type === 'aws'
                ? gt('Secret Access Key', {
                    context: 'Credential secret field label',
                  })
                : gt('Secret', { context: 'Credential secret field label' });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? (
              <T context="Dialog title editing stored credential">
                Edit Credential
              </T>
            ) : (
              <T context="Dialog title adding stored credential">
                Add Credential
              </T>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <CredentialAutoPauseFields
            enabled={autoPauseEnabled}
            threshold={autoPauseThreshold}
            error={errors.autoPauseThreshold}
            onEnabledChange={setAutoPauseEnabled}
            onThresholdChange={setAutoPauseThreshold}
          />
          {errors.form && (
            <p className="text-sm text-destructive">{errors.form}</p>
          )}

          <div className="space-y-2">
            <Label htmlFor="credentialType">
              <T context="Credential form field">Type</T>
            </Label>
            <Select
              value={type}
              onValueChange={(v) => {
                if (isCredentialTypeValue(v)) setType(v);
              }}
              disabled={isEditMode}
            >
              <SelectTrigger id="credentialType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {credentialTypes.map((ct) => (
                  <SelectItem key={ct.value} value={ct.value}>
                    {ct.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isEditMode && (
              <p className="text-xs text-muted-foreground">
                <T context="Help when credential type is locked after create">
                  Type cannot be changed after creation
                </T>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="credentialName">
              <T context="Credential form field">Name</T>
            </Label>
            <Input
              id="credentialName"
              placeholder={gt('e.g., Azure AD - Production', {
                context: 'Example credential name placeholder',
              })}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
            <p className="text-xs text-muted-foreground">
              <T context="Help text for credential display name">
                A descriptive label to identify this credential
              </T>
            </p>
          </div>

          {type === 'password' && (
            <div className="space-y-2">
              <Label htmlFor="credentialUsername">
                <T context="Credential username field for password type">
                  Username / Email
                </T>
              </Label>
              <Input
                id="credentialUsername"
                placeholder={gt('e.g., user@contoso.com', {
                  context: 'Example username placeholder',
                })}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                <T context="Help for password credential username">
                  The username or email associated with this password
                </T>
              </p>
            </div>
          )}

          {type === 'aws' && (
            <div className="space-y-2">
              <Label htmlFor="credentialAccessKeyId">
                <T context="Credential access key ID field for AWS type">
                  Access Key ID
                </T>
              </Label>
              <Input
                id="credentialAccessKeyId"
                placeholder={gt('e.g., AKIAIOSFODNN7EXAMPLE', {
                  context: 'Example AWS access key ID placeholder',
                })}
                value={accessKeyId}
                onChange={(e) => setAccessKeyId(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                <T context="Help for AWS credential access key ID">
                  The access key ID paired with this secret access key
                </T>
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="credentialSecret">{secretLabel}</Label>
            <Input
              id="credentialSecret"
              type="password"
              placeholder={
                isEditMode
                  ? gt('Leave blank to keep unchanged', {
                      context: 'Optional secret when editing credential',
                    })
                  : gt('••••••••', {
                      context: 'Masked secret placeholder for new credential',
                    })
              }
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
            {errors.secret && (
              <p className="text-sm text-destructive">{errors.secret}</p>
            )}
            {isEditMode && (
              <p className="text-xs text-muted-foreground">
                <T context="Help when editing credential secret">
                  Only enter a value if you want to change the secret
                </T>
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              resetForm();
              onClose();
            }}
          >
            <T context="Dismiss credential dialog">Cancel</T>
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createCredential.isPending || updateCredential.isPending}
          >
            {createCredential.isPending || updateCredential.isPending ? (
              <T context="Saving credential">Saving...</T>
            ) : (
              <T context="Save credential button">Save</T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
